import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Directive, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentFilters, DocumentQueryParams, DocumentQueryResult, Document, DocumentCategory, MessageService, DocumentCategoryLabel, QueryParamsService, CommonActionKey, SimpleDialogService } from 'common';
import { Subscription } from 'rxjs';
import { readWrite, UserPermissionId } from '../../user/user-permission/user-permission.data';
import { UserData } from '../../user/user.model';
import { AccountData } from '../../account/account.model';
import { DocumentsFilterService } from './document-filter.service';
import { DocumentBaseService } from 'common';
import { ApplicationData } from '../../application/application.model';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';

@UntilDestroy()
@Directive()
export abstract class DocumentListComponent<T extends DocumentBaseService> implements OnInit {
  @ViewChild(MatPaginator, { static: false })
  public paginator: MatPaginator;
  @ViewChild(MatSort)
  public sort: MatSort;

  data: DocumentListComponentData;
  queryParams: DocumentQueryParams;
  dataSource = new MatTableDataSource<Document>();
  readWrite: boolean = false;

  protected deleteSub: Subscription;
  tableColumns = [
    "createdOn",
    "filename",
    "category",
    "actions"
  ];

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected documentService: T,
    protected documentsFilterService: DocumentsFilterService,
    protected messageService: MessageService,
    protected dialog: MatDialog,
    protected userPermissionService: UserPermissionService,
    private queryParamsService: QueryParamsService,
    private simpleDialogService: SimpleDialogService,
  ) {
  }

  abstract ngOnInit();

  onInitBase(userPermissionId: UserPermissionId): void
  {
    this.userPermissionService.granted([readWrite(userPermissionId)])
      .subscribe(res => this.readWrite = res);

    this.route.queryParams.pipe(untilDestroyed(this))
      .subscribe(it => { this.queryParams = new DocumentQueryParams(this.queryParamsService.init(it)); });

    this.dataSource.data = this.data && this.data.documentQueryResult ? this.data.documentQueryResult.values : [];
    this.dataSource.paginator = this.paginator;

    if(this.paginator) {
      this.paginator.pageSize = this.queryParams.limit;
      this.paginator.pageIndex = this.queryParams.skip / this.queryParams.limit;
    }
  }

  sortData(): void {
    this.filter();
  }

  setPage(): void {
    this.filter();
  }

  filter(): void {
    this.documentsFilterService.filter(new DocumentFilters(this.queryParams), this.paginator, this.sort);
  }

  getCategory(category: DocumentCategory): string {
    return DocumentCategoryLabel.get(category);
  }

  download(fileId: number, parentRecordId: number): void {
    this.documentService.download(fileId, parentRecordId);
  }

  signature(fileId: number, parentRecordId: number): string {
    return `/account/${fileId}/documents/${parentRecordId}`;
  }

  delete(fileId: number, parentRecordId: number, filename: string): void {
    if(!readWrite) {
      this.messageService.error("You don't have permissions to delete this file");
      return;
    }

    this.simpleDialogService.openSimpleConfirmDialog(`Delete file ${filename}?`, "Confirm").subscribe((actionKey: string) => {
      if(actionKey === CommonActionKey.Confirm) {
        this.deleteSub = this.documentService.delete(fileId, parentRecordId)
        .subscribe({
          next: this.deleteSuccessHandler.bind(this),
          error: this.deleteErrorHandler.bind(this)
        });
      }
    });
  }

  protected deleteSubClear(): void {
    if(this.deleteSub)
      this.deleteSub.unsubscribe();
    this.deleteSub = null;
  }

  protected deleteErrorHandler(error: any): void {
    this.deleteSubClear();
    this.messageService.error(error);
  }

  protected deleteSuccessHandler(): void {
    this.deleteSubClear();
    location.reload();
  }
}

export interface DocumentListComponentData {
  userCurrent?: UserData;
  documentQueryResult: DocumentQueryResult;
}

export interface AccountDocumentListComponentData extends DocumentListComponentData {
  account?: AccountData
}

export interface ApplicationDocumentListComponentData extends DocumentListComponentData {
  application?: ApplicationData
}
